import React from "react";
import { toLocalDateString } from "@hex-insights/core";
import { projectName } from "../../../settings";
import {
	CourseSection,
	CourseSectionFilterInput,
	HomeRoomSection,
	HomeRoomSectionFilterInput,
	Student,
	StudentFilterInput,
	useStudentsClassYearLookupQuery,
} from "../../../Utilities";
import * as StudentAcademicTermEnrollmentUtilities from "../StudentAcademicTermEnrollment";
import * as StudentCourseSectionEnrollmentUtilities from "../StudentCourseSectionEnrollment";
import * as StudentHomeRoomSectionEnrollmentUtilities from "../StudentHomeRoomSectionEnrollment";

export function useClassYearCounts(date = new Date()) {
	const { data: studentsData } = useStudentsClassYearLookupQuery({
		variables: {
			filters: {
				graduationYearIsNull: false,
				studentAcademicTermEnrollments: [
					{
						academicTerm: [
							{
								startDateLTE: toLocalDateString(date),
								endDateGTE: toLocalDateString(date),
							},
						],
					},
				],
			},
		},
	});

	return React.useMemo(() => {
		if (!studentsData) {
			return {};
		}

		const counts = {};
		for (let i = 0; i < studentsData.studentConnection.edges.length; i++) {
			const student = studentsData.studentConnection.edges[i].node;
			if (!counts[student.yearsFromGraduation]) {
				counts[student.yearsFromGraduation] = 0;
			}
			counts[student.yearsFromGraduation]++;
		}

		return counts;
	}, [studentsData]);
}

export function getEnrolledInCourseSectionOnDateFilterInput(
	courseSectionID: CourseSection["id"],
	date: Date | string = new Date(),
): StudentFilterInput {
	return {
		studentAcademicTermEnrollments: [StudentAcademicTermEnrollmentUtilities.getEnrolledOnDateFilter(date)],
		studentCourseSectionEnrollments: [
			StudentCourseSectionEnrollmentUtilities.getEnrolledInCourseSectionOnDateFilterInput(courseSectionID, date),
		],
	};
}

export function getEnrolledInCourseSectionWithOnDateFilterInput(
	courseSectionFilterInput: CourseSectionFilterInput,
	date: Date | string = new Date(),
): StudentFilterInput {
	return {
		studentAcademicTermEnrollments: [StudentAcademicTermEnrollmentUtilities.getEnrolledOnDateFilter(date)],
		studentCourseSectionEnrollments: [
			StudentCourseSectionEnrollmentUtilities.getEnrolledInCourseSectionWithOnDateFilterInput(
				courseSectionFilterInput,
				date,
			),
		],
	};
}

export function getEnrolledInHomeRoomSectionOnDateFilterInput(
	homeRoomSectionID: HomeRoomSection["id"],
	date: Date | string = new Date(),
): StudentFilterInput {
	return {
		studentAcademicTermEnrollments: [StudentAcademicTermEnrollmentUtilities.getEnrolledOnDateFilter(date)],
		studentHomeRoomSectionEnrollments: [
			StudentHomeRoomSectionEnrollmentUtilities.getEnrolledInHomeRoomSectionOnDateFilterInput(homeRoomSectionID, date),
		],
	};
}

export function getEnrolledInHomeRoomSectionWithOnDateFilterInput(
	homeRoomSectionFilterInput: HomeRoomSectionFilterInput,
	date: Date | string = new Date(),
): StudentFilterInput {
	return {
		studentAcademicTermEnrollments: [StudentAcademicTermEnrollmentUtilities.getEnrolledOnDateFilter(date)],
		studentHomeRoomSectionEnrollments: [
			StudentHomeRoomSectionEnrollmentUtilities.getEnrolledInHomeRoomSectionWithOnDateFilterInput(
				homeRoomSectionFilterInput,
				date,
			),
		],
	};
}

export function getClassYear<T>(student: Pick<Student, "graduationYear">, classYears: Record<number, T>) {
	return (student.graduationYear ? classYears[student.graduationYear] : null) ?? null;
}

const romaniaSameNextClassYearIDs = new Set([
	"120259085219",
	"120259085320",
	"120259085332",
	"120259085345",
	"120259085371",
	"120259085327",
	"120259085383",
	"120259085009",
	"120259085073",
	"120259085304",
	"120259085330",
	"120259085400",
	"120259085255",
]);

const greeceSkipNextClassYearIDs = new Set(["120259084873"]);

const greeceSameNextYearClassIDs = new Set(["120259084319"]);

export function getNextClassYear<T>(student: Pick<Student, "id" | "graduationYear">, classYears: Record<number, T>) {
	if (projectName === "verita-romania" && romaniaSameNextClassYearIDs.has(student.id)) {
		// TODO temporary (2024-2025 only)
		return getClassYear(student, classYears);
	}

	if (projectName === "verita-greece") {
		if (greeceSkipNextClassYearIDs.has(student.id)) {
			// TODO temporary (2024-2025 only)
			return (student.graduationYear ? classYears[student.graduationYear - 2] : null) ?? null;
		}
		if (greeceSameNextYearClassIDs.has(student.id)) {
			// TODO temporary (2024-2025 only)
			return getClassYear(student, classYears);
		}
	}

	return (student.graduationYear ? classYears[student.graduationYear - 1] : null) ?? null;
}
